
/**
 * Module dependencies.
 */

import { find } from 'lodash';
import qs from 'qs';

/**
 * `Bucket` type.
 */

type Bucket = {
  hostname: string;
  resolveSize: (value: number) => number;
  widthFormatter: (value: number) => any;
};

/**
 * Sizes.
 */

const sizes = [16, 32, 64, 96, 128, 192, 256, 384, 512, 1024];

/**
 * Resolve approximate size.
 */

function resolveApproximateSize(width = 0): number {
  return find(sizes, size => size > width) ?? sizes[sizes.length - 1];
}

/**
 * Resolve mux approximate size.
 */

function resolveMuxApproximateSize(width = 0): number {
  const muxSizes = [16, 32, 64, 96, 128, 192, 256, 384, 512, 640];

  return find(muxSizes, size => size > width) ?? muxSizes[muxSizes.length - 1];
}

/**
 * Image bucket urls.
 */

const imageBucketUrls: Array<Bucket> = [{
  hostname: 'https://d1e2v2u8j6vuez.cloudfront.net',
  resolveSize: resolveApproximateSize,
  widthFormatter: (width: number) => ({ size: `${width}x` })
}, {
  hostname: 'https://image.mux.com',
  resolveSize: resolveMuxApproximateSize,
  widthFormatter: (width: number) => ({ width })
}, {
  hostname: 'https://ik.imagekit.io',
  resolveSize: resolveApproximateSize,
  widthFormatter: (width: number) => ({ tr: `n-w${width}` })
}];

/**
 * Get hostname config by uri.
 */

function getHostnameConfigByUri(uri: string): Bucket | null | undefined {
  return find(imageBucketUrls, ({ hostname }) => uri.startsWith(hostname));
}

/**
 * Export `resolveImageSize`.
 */

export function resolveImageSize(imageUrl: string | null | undefined, size: string): string | null | undefined {
  if (!imageUrl) {
    return imageUrl;
  }

  const hostnameConfig = getHostnameConfigByUri(imageUrl);

  if (!hostnameConfig) {
    return imageUrl;
  }

  const url = new URL(imageUrl);

  if (!url.hostname || !url.protocol) {
    return imageUrl;
  }

  const { resolveSize, widthFormatter } = hostnameConfig;

  url.search = qs.stringify({
    ...qs.parse(url.search, { ignoreQueryPrefix: true }),
    ...widthFormatter(resolveSize(parseInt(size, 10)))
  });

  return url.toString();
}
